import { Badge } from '@src/components/ui/badge';
import { Button } from '@src/components/ui/button';
import { MobileSelect } from '@src/components/ui/mobile-select';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectItem,
    MultiSelectTrigger,
    MultiSelectValue,
} from '@src/components/ui/multi-select';
import { cn } from '@src/lib/utils';

import {
    ProcessFieldTable,
    ServiceFieldTableRowDto,
    TableFieldSelectionRecordVM,
} from '@shared/types/process';

import {
    createEmptySelectionRecord,
    EMPTY_TABLE_ROW_SELECTION_RECORD_ID,
    getUsedSourceRecordIds,
    selectionRecordToOption,
} from './tableFieldUtils';
import { useSelectionRecords } from './useSelectionRecords';

import { useWindowDimensions } from '@shadcn-features/core/util/useWindowDimensions';
import { X } from 'lucide-react';
import { useMemo, useState, type SyntheticEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    fieldId: string;
    serviceId?: string;
    title?: ReactNode;
    placeholder?: string;
    tableOptions: ProcessFieldTable['options'];
    rows: ServiceFieldTableRowDto[];
    allowEmptyRow: boolean;
    disabled?: boolean;
    onAddRows: (records: TableFieldSelectionRecordVM[]) => void;
};

export function TableFieldToolbar({
    fieldId,
    serviceId,
    title,
    placeholder,
    tableOptions,
    rows,
    allowEmptyRow,
    disabled,
    onAddRows,
}: Props) {
    const { t } = useTranslation();
    const { isMobile } = useWindowDimensions();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState('');

    const showRegistryPicker = Boolean(tableOptions.registryId);

    const excludeRecordIds = useMemo(
        () => getUsedSourceRecordIds(rows),
        [rows],
    );

    const selectionQuery = useSelectionRecords({
        serviceId,
        serviceProcessFieldId: fieldId,
        excludeRecordIds,
        enabled: showRegistryPicker,
    });

    const emptySelectionRecord = useMemo(
        () => createEmptySelectionRecord(),
        [],
    );

    const options = useMemo(() => {
        const apiOptions = (selectionQuery.data ?? []).map(
            selectionRecordToOption,
        );

        if (!allowEmptyRow || searchValue.trim()) {
            return apiOptions;
        }

        return [
            {
                label: t('translation:emptyTableRow'),
                value: EMPTY_TABLE_ROW_SELECTION_RECORD_ID,
                record: emptySelectionRecord,
            },
            ...apiOptions,
        ];
    }, [
        allowEmptyRow,
        emptySelectionRecord,
        searchValue,
        selectionQuery.data,
        t,
    ]);

    const recordById = useMemo(() => {
        const map = new Map<string, TableFieldSelectionRecordVM>();
        if (allowEmptyRow) {
            map.set(EMPTY_TABLE_ROW_SELECTION_RECORD_ID, emptySelectionRecord);
        }
        (selectionQuery.data ?? []).forEach((record) => {
            map.set(record.recordId, record);
        });
        return map;
    }, [allowEmptyRow, emptySelectionRecord, selectionQuery.data]);

    const handleAdd = () => {
        const records = selectedIds
            .map((id) => recordById.get(id))
            .filter((record): record is TableFieldSelectionRecordVM =>
                Boolean(record),
            );

        if (!records.length) return;

        onAddRows(records);
        setSelectedIds([]);
    };

    const handleAddEmptyRow = () => {
        onAddRows([emptySelectionRecord]);
    };

    const removeSelected = (event: SyntheticEvent, value: string) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled)
            setSelectedIds((prev) => prev.filter((id) => id !== value));
    };

    const renderSelectedValues = (values: string[]) => (
        <div className='office-scrollbar flex min-w-0 flex-1 gap-1 overflow-x-auto'>
            {values.map((value) => {
                const label =
                    value === EMPTY_TABLE_ROW_SELECTION_RECORD_ID
                        ? t('translation:emptyTableRow')
                        : options.find((option) => option.value === value)
                              ?.label;
                return (
                    <Badge
                        key={value}
                        data-selected-item
                        className='flex h-7! w-[116px] shrink-0 items-center gap-1 rounded-lg! border-(--color-multiselect-tag-border)! bg-(--color-body)! py-0.5! pr-1! pl-2! text-xs! font-normal! text-(--color-select-text)!'
                    >
                        <span className='min-w-0 flex-1 truncate text-xs! font-normal!'>
                            {label}
                        </span>
                        <span
                            role='button'
                            tabIndex={disabled ? -1 : 0}
                            aria-disabled={disabled}
                            aria-label={t('translation:reset') + ': ' + label}
                            className='flex size-4 shrink-0 items-center justify-center text-(--color-card-subtitle)'
                            onClick={(event) => removeSelected(event, value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ')
                                    removeSelected(event, value);
                            }}
                        >
                            <X className='size-2.5!' />
                        </span>
                    </Badge>
                );
            })}
        </div>
    );

    if (!showRegistryPicker) {
        if (!allowEmptyRow) return null;

        return (
            <Button
                className='h-auto! w-fit border-0 bg-(--color-primary-1)! text-(--color-brand-green)! shadow-none hover:bg-(--color-primary-1)! max-md:h-[42px]! max-md:w-full'
                disabled={disabled}
                fontWeight={600}
                height='auto'
                paddingX={16}
                paddingY={7.5}
                type='button'
                onClick={handleAddEmptyRow}
            >
                {t('translation:add')}
            </Button>
        );
    }

    return (
        <div className='flex w-full flex-col gap-2 md:flex-row'>
            <div className='min-w-0 flex-1'>
                <MultiSelect
                    values={selectedIds}
                    onValuesChange={setSelectedIds}
                    mobileContent={
                        isMobile && (
                            <MobileSelect
                                multiple
                                title={title ?? t('translation:select')}
                                placeholder={placeholder}
                                aria-label={
                                    placeholder || t('translation:select')
                                }
                                options={options}
                                values={selectedIds}
                                onValuesChange={setSelectedIds}
                                searchValue={searchValue}
                                onSearchChange={setSearchValue}
                                loading={selectionQuery.isFetching}
                                disabled={disabled || selectionQuery.isFetching}
                                renderValue={
                                    selectedIds.length
                                        ? renderSelectedValues(selectedIds)
                                        : undefined
                                }
                                className='h-[52px] min-h-[52px] [&>svg]:size-5'
                                optionClassName='font-inter items-start py-5 leading-[normal] [&>span:first-child]:mt-1'
                                contentClassName='font-inter [&_[data-slot=mobile-select-apply]]:leading-[normal]'
                            />
                        )
                    }
                >
                    <MultiSelectTrigger
                        allowClear={isMobile}
                        aria-label={placeholder || t('translation:select')}
                        disabled={disabled || selectionQuery.isFetching}
                        className={cn(
                            'h-[52px]! min-h-[52px]! w-full rounded-lg! border! border-select-border! bg-input-light! px-3! py-4! shadow-none! md:border-(--color-select-border)! md:bg-(--color-input-expose)! md:font-inter',
                            '[&>svg:last-child]:size-5! [&>svg:last-child]:opacity-100!',
                            'justify-between!',
                        )}
                    >
                        <MultiSelectValue
                            placeholder={placeholder}
                            customRender={
                                isMobile ? undefined : renderSelectedValues
                            }
                        />
                    </MultiSelectTrigger>
                    <MultiSelectContent
                        loading={selectionQuery.isFetching}
                        search={{
                            placeholder: t('translation:search'),
                            controlled: {
                                searchValue,
                                setSearchValue: (value) =>
                                    setSearchValue(value ?? ''),
                            },
                        }}
                    >
                        {options.map((option) => (
                            <MultiSelectItem
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </MultiSelectItem>
                        ))}
                    </MultiSelectContent>
                </MultiSelect>
            </div>

            <button
                className={cn(
                    'inline-flex h-[42px] shrink-0 items-center justify-center rounded-lg bg-(--color-primary-1) px-4 text-sm font-semibold text-(--color-brand-green) disabled:cursor-not-allowed disabled:opacity-50 md:h-[53px]',
                    rows.length === 0 && 'h-[53px]',
                )}
                disabled={disabled || selectedIds.length === 0}
                type='button'
                onClick={handleAdd}
            >
                {t('translation:add')}
            </button>
        </div>
    );
}
